import { useEffect } from "react";
import { Link } from "react-router";
import { styled } from "styled-components";

import { StyledModalText, UIButton } from "common/components";
import { useDispatch, useMemoisedSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { interpolate } from "domains/language/interpolate";
import { deactivateModal } from "domains/modal/actions";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { ManageTopicItem } from "domains/monitor/filter/components/ManageTopicItem";
import { fetchSuggestions } from "domains/monitor/suggestions/actions";
import { createSuggestedTopicsSelector, getSuggestedTopicsIds } from "domains/monitor/suggestions/selectors";
import { createTopicsSelector, getFollowedTopicsIds } from "entities/topics/selectors";

const StyledContainer = styled.div`
  margin: 1rem 0;
`;

const StyledSectionTitleText = styled.h2`
  font-size: 1.5rem;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.textFade};
`;

const StyledSectionTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.blue};
  text-decoration: none;
  font-weight: 500;
`;

const StyledMoreLink = styled(Link)`
  text-decoration: none;
`;

export const ManageTopicsModal = () => {
  const dispatch = useDispatch();
  const followingTopics = useMemoisedSelector(getFollowedTopicsIds, createTopicsSelector);
  const suggestions = useMemoisedSelector(getSuggestedTopicsIds, createSuggestedTopicsSelector);
  const translate = useTranslate();

  const onDismiss = () => dispatch(deactivateModal());

  const discover = (value: string) => (
    <StyledLink to="/topics" onClick={onDismiss}>
      {value}
    </StyledLink>
  );

  const descriptionText = interpolate(translate("app.monitor.manage_topics.description"), { discover });

  useEffect(() => {
    if (suggestions.length === 0) {
      dispatch(fetchSuggestions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <>
      <ModalHeader title={translate("app.discover.manage_topics")} />
      <StyledModalText>{descriptionText}</StyledModalText>
      {followingTopics.length > 0 && (
        <>
          <StyledSectionTitleText>{translate("app.monitor.following_topics")}</StyledSectionTitleText>
          {followingTopics.map((topic) => (
            <ManageTopicItem
              id={topic.id}
              images={topic.images}
              isFollowing
              isFollowingUpdating={topic.isFollowingUpdating}
              key={topic.id}
              name={topic.name}
            />
          ))}
        </>
      )}
      {suggestions.length > 0 && (
        <StyledContainer>
          <StyledSectionTitle>
            <StyledSectionTitleText>{translate("app.monitor.suggested_topics.title")}</StyledSectionTitleText>
            <StyledMoreLink to="/topics">
              <UIButton onClick={onDismiss} variant="outlined">
                {translate("app.monitor.see_more_topics")}
              </UIButton>
            </StyledMoreLink>
          </StyledSectionTitle>
          {suggestions.map((topic) => (
            <ManageTopicItem
              key={topic.id}
              id={topic.id}
              name={topic.name}
              images={topic.images}
              isFollowing={false}
              isFollowingUpdating={topic.isFollowingUpdating}
            />
          ))}
        </StyledContainer>
      )}
    </>
  );
};
